import { fromJS, List } from 'immutable';
import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';
import isUndefined from 'lodash/isUndefined';

import Logger from '../tools/Logger';

const log = Logger.child({ component: 'lib/NumberUtils' });
const TEN_MILLION = 10000000;
const ONE_MILLION = 1000000;
const ONE_HUNDRED_THOUSAND = 100000;
const TEN_THOUSAND = 10000;
const ONE_THOUSAND = 1000;
const ONE_HUNDRED = 100;

/**
 * numberToHumanSize - takes a number like 567000 and converts to '567K'.
 *
 * @param  {number} rawInteger - an integer that you want to convert to this format
 * @param {string} [zeroFormat] - a value to return if rawInteger === 0
 * @returns {string}
 */

export function numberToHumanSize(rawInteger: number, zeroFormat?: string): string {
  if (Number.isNaN(rawInteger)) {
    return '';
  } else if (rawInteger === 0 && typeof zeroFormat !== 'undefined') {
    return zeroFormat;
  } else if (rawInteger >= TEN_MILLION) {
    // If over 10 million, format number to have no decimal point. Example: "25M Listeners"
    return Math.floor(rawInteger / ONE_MILLION) + 'M';
  } else if (rawInteger >= ONE_MILLION) {
    // If between 10 million and 1 million, format number to one decimal point. Example: "2.5M Listeners"
    return Math.floor(rawInteger / ONE_HUNDRED_THOUSAND) / 10 + 'M';
  } else if (rawInteger >= TEN_THOUSAND) {
    // If between 1 million and 10 thousand, format number to have no decimal point. Example: "567K Listeners"
    return Math.floor(rawInteger / ONE_THOUSAND) + 'K';
  } else if (rawInteger >= ONE_THOUSAND) {
    // If between 10 thousand and 1 thousand, format number to have one decimal point. Example: "7.4K Listeners"
    return Math.floor(rawInteger / ONE_HUNDRED) / 10 + 'K';
  }

  // If under 1 thousand, show raw integer. Example: "234 Listeners"
  return rawInteger + '';
}

/**
 * Simple function to clamp a number between two others
 *
 * @param {number} lowerBound - returned if numberToClamp < lowerBound
 * @param {number} numberToClamp - will be returned unless it is >upperBound or <lowerBound
 * @param {number} upperBound - returned if numberToClamp > upperBound
 * @returns {number} the number clamped between the lower and upper bounds
 */
export function clampNumber(lowerBound: number, numberToClamp: number, upperBound: number): number {
  return Math.max(lowerBound, Math.min(upperBound, numberToClamp));
}

/**
 * Validate a birthdate input. API expects integer.
 *
 * @param {number | string | null} num - day/month/year number
 * @returns {number | null} the parsed number of null if unrecognized
 */
export function parseNumber(num: number | string | null): number | null {
  if (isUndefined(num) || num === '') {
    return null;
  }
  if (isNumber(num) || num === null) {
    return num;
  }
  const parsed = parseInt(num, 10);
  if (isNaN(parsed)) {
    log.error(`Unable to parse date input: ${num}`);
    return null;
  }
  return parsed;
}

/*
 * Taken from http://stackoverflow.com/a/6832721/11236, compares versions
 * like 1.7.01 to 1.8.01 and returns a -1 if the first is less, 0 if they're the same
 * and 1 if the first is greater. Will return NaN if input is invalid.
 */
export function compareVersionNumbers(v1: string, v2: string): number {
  const zeroExtend = true;
  if (!v1 || !v2) {
    return NaN;
  }
  const v1parts = v1.toString().split('.');
  const v2parts = v2.toString().split('.');
  let returnNaN = false;

  // We're going to allow something like 43b as valid, but will strip the letter later
  function isValidPart(x: string): boolean {
    return /^\d+/.test(x);
  }

  function getLeadingNumber(x: string): string | undefined {
    const match = x.match(/(^\d+)/);
    return match ? match[0] : undefined;
  }

  function parsePartsToNumber(parts: string[]): number[] {
    return parts.map((numString: string): number => {
      const parsedString = Number(getLeadingNumber(numString));
      if (isNaN(parsedString)) {
        returnNaN = true;
      }
      return parsedString;
    });
  }

  if (!v1parts.every(isValidPart) || !v2parts.every(isValidPart)) {
    return NaN;
  }

  if (zeroExtend) {
    while (v1parts.length < v2parts.length) v1parts.push('0');
    while (v2parts.length < v1parts.length) v2parts.push('0');
  }

  const v1partsNum = parsePartsToNumber(v1parts);
  const v2partsNum = parsePartsToNumber(v2parts);

  if (returnNaN) {
    return NaN;
  }

  for (let i = 0; i < v1partsNum.length; ++i) {
    if (v2partsNum.length === i) {
      return 1;
    }

    if (v1partsNum[i] === v2partsNum[i]) {
      continue;
    } else if (v1partsNum[i] > v2partsNum[i]) {
      return 1;
    } else {
      return -1;
    }
  }

  if (v1partsNum.length !== v2partsNum.length) {
    return -1;
  }

  return 0;
}

export function getPseudoRandom(): string {
  return Math.random().toString().substr(2, 12);
}

const MAX_SEED = 100000000000;
let lcgSeed = Math.floor(Math.random() * MAX_SEED);
// UNUSED
export function getSeed(): $TSFixMe {
  // just increments a number [0, 1000000000000]
  return (lcgSeed = (lcgSeed + 1) % MAX_SEED);
}

// The BSD formula for a linear congruential generator
// https://rosettacode.org/wiki/Linear_congruential_generator
export function createRand(seed: $TSFixMe): () => number {
  const m = 2147483648;
  const a = 1103515245;
  const c = 12345;

  let z = seed || 3;
  return (): number => {
    z = (a * z + c) % m;
    return z / m;
  };
}

/*
 * Fisher-Yates shuffle function which either uses a pseudo random sequence
 * given a seed or Math.random if no seed is provided. Also allows a start index, so
 * the first number will be fixed and the rest will be shuffled.
 * Input:
 *  list - immutable list, or array
 *  seed (optional) - the seed to use in the pseudo random generator
 *  startIndex - the index to start the shuffled list from
 */

interface ShuffleHelperObj {
  list: List<unknown> | Array<unknown>;
  seed?: number;
  startIndex?: null | number;
}

export function shuffle({ list, seed, startIndex = null }: ShuffleHelperObj): List<number> {
  let arr = List.isList(list) ? list : fromJS(list);
  let temp;
  const rand = seed ? createRand(seed) : Math.random;
  let randIdx;
  let startVal;
  if (startIndex != null && startIndex < arr.size && startIndex >= 0) {
    startVal = arr.get(startIndex);
    arr = arr.splice(startIndex, 1);
  }
  let endDex = arr.size;
  arr = arr.withMutations((arrMutable: $TSFixMe) => {
    while (endDex > 0) {
      randIdx = Math.floor(rand() * endDex--);
      temp = arrMutable.get(endDex);
      arrMutable.set(endDex, arrMutable.get(randIdx));
      arrMutable.set(randIdx, temp);
    }
    return arrMutable;
  });
  return startVal ? arr.unshift(startVal) : arr;
}
